import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import BlogSeo from './BlogSeo';
import BlogNotFound from './BlogNotFound';
import { getBlogPostByLegacyId, getBlogPostBySlug } from '../blogs/blogConfig';
import { getPostMetadata } from '../blogs/blogPostMetadata';
import { enhanceFiguresForMobile, renderV2ArticleHtml, resolveRelatedPosts, wrapFaqAsAccordion } from '../blogs/blogV2Layout';
import { normalizeArticleHtml } from '../utils/normalizeArticleHtml';
import { getAmitasoftSiteUrls } from '../utils/amitasoftSiteUrls';

const extractHtmlContent = (html) => {
    const publicRootMatch = html.match(/<div[^>]*id=["']public-root["'][^>]*>([\s\S]*?)<\/div>/i);
    if (publicRootMatch) {
        return publicRootMatch[1];
    }

    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
        return bodyMatch[1];
    }

    return html;
};

const formatDisplayDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'America/Los_Angeles',
    }).format(date);
};

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const legacyId = /^\d+$/.test(slug || '') ? parseInt(slug, 10) : null;
    const blog = legacyId ? getBlogPostByLegacyId(legacyId) : getBlogPostBySlug(slug);
    const [content, setContent] = useState('');

    useEffect(() => {
        if (legacyId && blog) {
            navigate(`/blog/${blog.slug}/`, { replace: true });
        }
    }, [legacyId, blog, navigate]);

    useEffect(() => {
        if (!blog) {
            setContent('');
            return;
        }

        fetch(`${process.env.PUBLIC_URL}${blog.contentPath}`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to load content: ${response.status}`);
                }
                return response.text();
            })
            .then((data) => {
                const siteUrls = getAmitasoftSiteUrls();
                let html = normalizeArticleHtml(extractHtmlContent(data), siteUrls, {
                    stripTitle: blog.title,
                    stripLeadingImages: blog.contentPath.startsWith('/blogs/') ? 1 : 0,
                    publicUrlPrefix: process.env.PUBLIC_URL || '/blog',
                });
                if (Array.isArray(blog.faqPage) && blog.faqPage.length) {
                    html = wrapFaqAsAccordion(html);
                }
                html = enhanceFiguresForMobile(html);
                setContent(html);
            })
            .catch((error) => {
                console.error('Error fetching blog content:', error);
                setContent('');
            });
    }, [blog]);

    const meta = useMemo(() => {
        if (!blog) return null;
        return getPostMetadata(blog.slug);
    }, [blog]);

    const relatedPosts = useMemo(() => {
        if (!meta) return [];
        return resolveRelatedPosts(meta.relatedSlugs, getBlogPostBySlug);
    }, [meta]);

    const articleHtml = useMemo(() => {
        if (!blog || !meta) return '';
        const imagePath = `${process.env.PUBLIC_URL}${blog.image}`;
        return renderV2ArticleHtml({
            post: blog,
            meta,
            relatedPosts,
            bodyHtml: content,
            imagePath,
            publishedIso: blog.publishedDate,
            publishedLabel: formatDisplayDate(blog.publishedDate),
        });
    }, [blog, meta, relatedPosts, content]);

    if (!blog) {
        return (
            <>
                <BlogSeo
                    title="Page not found | AmitaSoft Blogs"
                    description="The requested blog article could not be found."
                    canonicalPath="/blog/404"
                    noIndex
                />
                <BlogNotFound />
            </>
        );
    }

    if (blog.redirectTo) {
        return <Navigate to={blog.redirectTo} replace />;
    }

    return (
        <>
            <BlogSeo
                title={blog.seoTitle || `${blog.title} | AmitaSoft Blogs`}
                description={blog.seoDescription || blog.description}
                canonicalPath={blog.canonicalPath || `/blog/${blog.slug}/`}
                image={blog.image}
                noIndex={Boolean(blog.noIndex)}
            />
            <main>
                <div
                    id="blog-v2-preview"
                    className="bg-white"
                    dangerouslySetInnerHTML={{ __html: articleHtml }}
                />
            </main>
        </>
    );
};

export default BlogDetail;
